import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './icon/index.ts';
import styles from './Select.module.css';

/**
 * Design system § 14 — Select.
 *
 * A TRIGGER PLUS A LISTBOX, not a native `<select>`, and the reason is the
 * popover: an operating-system option list takes none of §§ 3–8 and is the one
 * surface in the product the design system cannot reach. § 14 records that
 * change and what it costs — everything the platform gave for free is restated
 * here by hand, and the two are checked against each other below.
 *
 * The trigger is Input's box (`radius-md`, `--control-height`, the same
 * gradient-border edge and the same `default`/`focus`/`error`/`disabled`
 * states), plus the chevron well that tells a select from a text field across a
 * form. The listbox is a `--surface-solid` popover with its own border and
 * `--shadow-2`.
 *
 * WHAT THE PLATFORM USED TO OWN, AND WHERE IT IS NOW:
 *
 *   - roles — `combobox` on the trigger, `listbox` on the popover, `option` on
 *     each row, so assistive technology hears a select rather than a button
 *     next to a list;
 *   - focus — it never leaves the trigger. The active option is named by
 *     `aria-activedescendant`, which is what keeps § 12's one-ring rule true
 *     with a popover open;
 *   - keys — Enter/Space/↓/↑ open; ↓/↑/Home/End move; letters jump; Enter
 *     commits; Escape restores; Tab commits and moves on; ↓/↑ closed step
 *     through the values without opening;
 *   - dismissal — a pointer outside the control closes it, on the value that
 *     was already there.
 *
 * The label lives in Field, exactly as it does for Input.
 */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  id: string;
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  /** Shown while nothing is chosen — for a field with no default yet. */
  placeholder?: string;
  disabled?: boolean;
  /** Set when Field is rendering an error for this control. */
  invalid?: boolean;
  required?: boolean;
  describedBy?: string;
  /**
   * § 16 — `compact` is the toolbar and table-row height, for a control that
   * is one of a dozen on a row rather than the screen's own decision.
   */
  size?: 'default' | 'compact';
  /** The accessible name, where no visible `Field` label supplies one. */
  ariaLabel?: string;
}

/** The next selectable index in `direction`, or the current one if there is none. */
function step(options: readonly SelectOption[], from: number, direction: 1 | -1): number {
  for (let index = from + direction; index >= 0 && index < options.length; index += direction) {
    if (!options[index]?.disabled) return index;
  }
  return from;
}

/** The first selectable index, or the last one, depending on `edge`. */
function edgeIndex(options: readonly SelectOption[], edge: 'first' | 'last'): number {
  const order = edge === 'first' ? options.map((_, i) => i) : options.map((_, i) => i).reverse();
  return order.find((index) => !options[index]?.disabled) ?? -1;
}

export function Select({
  id,
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  invalid = false,
  required,
  describedBy,
  size = 'default',
  ariaLabel,
}: SelectProps) {
  const listId = `${useId()}-listbox`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [placement, setPlacement] = useState<'below' | 'above'>('below');

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  /** Typed letters, cleared after a pause — the platform's own typeahead. */
  const typed = useRef<{ buffer: string; at: number }>({ buffer: '', at: 0 });

  const selected = options.findIndex((option) => option.value === value);
  const current = selected >= 0 ? options[selected] : undefined;

  const openList = useCallback(
    (start?: number) => {
      if (disabled) return;
      setActive(start ?? (selected >= 0 ? selected : edgeIndex(options, 'first')));
      setOpen(true);
    },
    [disabled, options, selected],
  );

  const commit = useCallback(
    (index: number) => {
      const option = options[index];
      setOpen(false);
      if (!option || option.disabled) return;
      if (option.value !== value) onChange(option.value);
    },
    [options, onChange, value],
  );

  /*
   * Opens upward when the popover would not fit below. Measured rather than
   * assumed because the first caller is a Drawer, where a field near the
   * bottom of a scrolling panel is the normal case rather than the edge one.
   * `useLayoutEffect` so the flip happens in the same frame the list appears.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const box = triggerRef.current?.getBoundingClientRect();
    const list = listRef.current?.offsetHeight ?? 0;
    if (!box) return;
    const below = window.innerHeight - box.bottom;
    setPlacement(below < list + 8 && box.top > below ? 'above' : 'below');
  }, [open]);

  /* Keeps the active option in view while the arrows move past the fold.
     Feature-detected rather than assumed: `scrollIntoView` is missing in
     jsdom and in some embedded engines, and a keyboard that cannot move
     through a list is a worse failure than one that does not scroll. */
  useEffect(() => {
    if (!open || active < 0) return;
    const option = listRef.current?.children[active];
    if (option instanceof HTMLElement && typeof option.scrollIntoView === 'function') {
      option.scrollIntoView({ block: 'nearest' });
    }
  }, [open, active]);

  /* A pointer outside the control closes it on the value that was already
     there — the same dismissal a native option list has. */
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  /** Jumps to the option starting with what has just been typed. */
  const typeahead = useCallback(
    (key: string): number => {
      const now = Date.now();
      const buffer = now - typed.current.at < 600 ? typed.current.buffer + key : key;
      typed.current = { buffer, at: now };

      const match = options.findIndex(
        (option) => !option.disabled && option.label.toLocaleLowerCase().startsWith(buffer),
      );
      return match;
    },
    [options],
  );

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    const { key } = event;

    if (!open) {
      if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        openList(key === 'ArrowUp' ? edgeIndex(options, 'last') : undefined);
        return;
      }
      // Closed, the arrows step through the values without opening, and a
      // typed letter picks one — both are what a native select does.
      if (key.length === 1 && /\S/.test(key)) {
        const match = typeahead(key.toLocaleLowerCase());
        if (match >= 0) commit(match);
      }
      return;
    }

    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((index) => step(options, index, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((index) => step(options, index, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActive(edgeIndex(options, 'first'));
        break;
      case 'End':
        event.preventDefault();
        setActive(edgeIndex(options, 'last'));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(active);
        break;
      case 'Escape':
        event.preventDefault();
        // Restores rather than commits: Escape means "never mind".
        setOpen(false);
        break;
      case 'Tab':
        // Not prevented — Tab commits and moves on, as it does natively.
        commit(active);
        break;
      default:
        if (key.length === 1 && /\S/.test(key)) {
          const match = typeahead(key.toLocaleLowerCase());
          if (match >= 0) setActive(match);
        }
    }
  }

  const state = disabled ? 'disabled' : invalid ? 'error' : 'default';

  return (
    <span className={styles.wrapper} ref={wrapperRef}>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        data-state={state}
        data-size={size}
        data-open={open || undefined}
        data-placeholder={current ? undefined : 'true'}
        disabled={disabled}
        role="combobox"
        aria-controls={listId}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-activedescendant={open && active >= 0 ? `${id}-option-${active}` : undefined}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-label={ariaLabel}
        aria-describedby={describedBy ?? (invalid ? `${id}-error` : undefined)}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
      >
        {/* `title` so a label the box truncates is still readable on hover;
            the full text is in the listbox either way. */}
        <span className={styles.value} title={current?.label}>
          {current?.label ?? placeholder ?? ''}
        </span>
        <span className={styles.well} aria-hidden="true">
          <Icon name="chevron-down" size={20} />
        </span>
      </button>

      {open ? (
        <ul
          id={listId}
          ref={listRef}
          className={styles.list}
          data-placement={placement}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${id}-option-${index}`}
              className={styles.option}
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled || undefined}
              data-active={index === active || undefined}
              data-selected={option.value === value || undefined}
              data-disabled={option.disabled || undefined}
              /* `pointerdown` rather than `click`: the window listener above
                 fires on the same phase, and committing here keeps the two
                 from racing on which one closes the list. */
              onPointerDown={(event) => {
                event.preventDefault();
                commit(index);
                triggerRef.current?.focus();
              }}
              onPointerEnter={() => !option.disabled && setActive(index)}
            >
              <span className={styles.optionLabel}>{option.label}</span>
              {option.value === value ? (
                <span className={styles.check} aria-hidden="true">
                  <Icon name="check" size={16} />
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}
